import {
  useGenericRefinement,
  BaseRefinementItem,
  RefinementConfig,
  RawRefinementItem,
} from '../../Algolia/useGenericRefinement';
import { QUESTION_TYPES } from './GenericQuestionTypeSelector';

// Generate mapping from question type values to display labels from the source of truth
// This handles the mapping between internal codes and user-friendly labels
export const QUESTION_TYPE_MAP: Record<string, string> = QUESTION_TYPES.reduce(
  (acc, questionType) => {
    acc[questionType.value] = questionType.label;
    return acc;
  },
  {} as Record<string, string>,
);

// Generate reverse mapping from labels to values (for Algolia data that might use labels)
export const QUESTION_TYPE_LABEL_TO_VALUE_MAP: Record<string, string> =
  QUESTION_TYPES.reduce((acc, questionType) => {
    acc[questionType.label] = questionType.value;
    return acc;
  }, {} as Record<string, string>);

export interface QuestionTypeRefinementItem extends BaseRefinementItem {
  questionTypeValue: string;
  questionTypeName: string;
}

export interface QuestionTypeMapping {
  typeMap: typeof QUESTION_TYPE_MAP;
  labelToValueMap: typeof QUESTION_TYPE_LABEL_TO_VALUE_MAP;
}

export interface QuestionTypeRefinementResult {
  items: QuestionTypeRefinementItem[];
  refine: (value: string) => void;
  refineMultiple: (values: string[]) => void;
  clearRefinements: () => void;
  isSearchStalled: boolean;
  canRefine: boolean;
  getCountForType: (type: string) => number;
  getCountForValue: (value: string) => number;
  getTotalCount: () => number;
}

// Configuration for QuestionType refinement
const questionTypeConfig: RefinementConfig<
  QuestionTypeRefinementItem,
  QuestionTypeMapping
> = {
  attribute: 'Question Type',
  mapping: {
    typeMap: QUESTION_TYPE_MAP,
    labelToValueMap: QUESTION_TYPE_LABEL_TO_VALUE_MAP,
  },

  validateItem: (item: RawRefinementItem, mapping: QuestionTypeMapping): boolean => {
    const isValidCode = mapping.typeMap[item.value];
    const isValidLabel = mapping.labelToValueMap[item.value];
    return !!(isValidCode || isValidLabel);
  },

  transformItem: (
    item: RawRefinementItem,
    mapping: QuestionTypeMapping,
  ): QuestionTypeRefinementItem => {
    let questionTypeValue: string;
    let questionTypeName: string;

    // Determine if this is a code or a label
    if (mapping.typeMap[item.value]) {
      // It's a code
      questionTypeValue = item.value;
      questionTypeName = mapping.typeMap[item.value];
    } else if (mapping.labelToValueMap[item.value]) {
      // It's a label, convert to code
      questionTypeValue = mapping.labelToValueMap[item.value];
      questionTypeName = item.value;
    } else {
      // Fallback - use as-is
      questionTypeValue = item.value;
      questionTypeName = item.value;
    }

    return {
      label: questionTypeName,
      value: questionTypeValue,
      count: item.count,
      isRefined: item.isRefined,
      questionTypeValue,
      questionTypeName,
    };
  },

  sortItems: (a: QuestionTypeRefinementItem, b: QuestionTypeRefinementItem): number =>
    a.questionTypeName.localeCompare(b.questionTypeName),

  groupItems: (items: QuestionTypeRefinementItem[]): QuestionTypeRefinementItem[] => {
    // Group items by question type and combine counts
    const typeGroups: Record<string, QuestionTypeRefinementItem[]> = {};

    items.forEach((item) => {
      if (!typeGroups[item.questionTypeValue]) {
        typeGroups[item.questionTypeValue] = [];
      }
      typeGroups[item.questionTypeValue].push(item);
    });

    // Convert groups to final format, combining counts
    return Object.entries(typeGroups)
      .map(([value, groupItems]) => {
        const totalCount = groupItems.reduce((sum, item) => sum + item.count, 0);
        const isRefined = groupItems.some((item) => item.isRefined);
        const firstItem = groupItems[0];

        return {
          label: firstItem.questionTypeName,
          value,
          count: totalCount,
          isRefined,
          questionTypeValue: value,
          questionTypeName: firstItem.questionTypeName,
        };
      })
      .sort((a, b) => a.questionTypeName.localeCompare(b.questionTypeName));
  },

  getters: {
    getCountForType: (items: QuestionTypeRefinementItem[], type: string): number => {
      // Check both by value code and by name
      const item = items.find(
        (questionTypeItem) =>
          questionTypeItem.questionTypeValue === type ||
          questionTypeItem.questionTypeName === type,
      );
      return item?.count || 0;
    },
    getCountForValue: (items: QuestionTypeRefinementItem[], value: string): number => {
      const item = items.find((questionTypeItem) => questionTypeItem.value === value);
      return item?.count || 0;
    },
  },
};

/**
 * Custom hook for Question Type refinements
 * Provides Algolia facet data specifically formatted for Question Types
 * Handles both value codes (mcq, mrq, etc.) and display labels
 */
export const useQuestionTypeRefinement = (
  attribute = 'Question Type',
): QuestionTypeRefinementResult => {
  const config = { ...questionTypeConfig, attribute };
  const result =
    useGenericRefinement<QuestionTypeRefinementItem, QuestionTypeMapping>(config);

  return {
    ...result,
    getCountForType: result.getCountForType,
    getCountForValue: result.getCountForValue,
  };
};
